//* *
// TO DO: Fix the eslint errors */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useEffect, useState } from "react";
import { Grid, Container, Toolbar } from "@mui/material";
import {
  closestCenter,
  DndContext,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useApi } from "@platform/api";
import { Button } from "@platform/design-system";

import useToastNotificationService from "../../hooks/useToastNotificationService";
import { Loader } from "../../components";

import CarriersTable from "./carriersTable";
import { Carrier, Columns, DragAndDropCarrier } from "./types";
import { isCarrierSelected } from "./helpers";
import CarrierTableRow from "./carrierTableRow";
import ErrorMessage from "./errorMessage";

const COLUMNS: Record<string, Columns> = {
  SELECTED: "selected",
  UNSELECTED: "unselected",
};

const MAX_SELECTED_CARRIERS = 19;

function CarriersOrder() {
  const [state, setState] = useState<PrepareCarriersOrderResponse>(() => ({
    [COLUMNS.SELECTED]: [],
    [COLUMNS.UNSELECTED]: [],
  }));
  const [activeDraggedCarrier, setActiveDraggedCarrier] =
    useState<DragAndDropCarrier | null>(null);
  const { createToastNotification } = useToastNotificationService();
  const axiosInstance = useApi();

  const query = useQuery<Carrier[], Error, PrepareCarriersOrderResponse>({
    queryKey: ["prepareCarriersOrder"],
    staleTime: Infinity,
    queryFn: () =>
      axiosInstance
        .post("/brokerportal/v1/prepareCarriersOrder")
        .then((res) => res.data),
    select: querySelect,
  });

  const mutation = useMutation<void, Error, { carrierIds: number[] }>({
    mutationFn: (body) =>
      axiosInstance.patch("/brokerportal/v1/setCarriersOrder", body),
    onSuccess: () => {
      createToastNotification({
        severity: "success",
        message: "Carriers order saved!",
      });
    },
  });

  useEffect(() => {
    if (query.data) {
      setState(query.data);
    }
  }, [query.data]);

  function handleSubmit() {
    mutation.mutate({
      carrierIds: state[COLUMNS.SELECTED].map((s) => s.id),
    });
  }

  function handleDragStart(event: DragStartEvent) {
    if (event.active.data.current?.carrier) {
      setActiveDraggedCarrier(event.active.data.current as DragAndDropCarrier);
      return;
    }
  }

  function handleDragOver({ active, over }: DragOverEvent) {
    // return if element is dropped outside of a droppable container
    if (!over) {
      return;
    }

    // return if the active element is dropped on itself
    if (active.id === over.id) {
      return;
    }

    const source = active.data.current as DragAndDropCarrier;
    const destination = over.data.current as DragAndDropCarrier;

    if (
      state[COLUMNS.SELECTED].length > MAX_SELECTED_CARRIERS &&
      source.column === COLUMNS.UNSELECTED &&
      destination.column === COLUMNS.SELECTED
    ) {
      return;
    }

    if (source.column === destination.column) {
      setState((preState) => ({
        ...preState,
        [source.column]: arrayMove(
          preState[source.column],
          source.index,
          destination.index,
        ),
      }));
    } else {
      setState((prevState) => {
        const sourceColumn = [...prevState[source.column]];
        const destinationColumn = [...prevState[destination.column]];
        const draggable = sourceColumn[source.index];
        sourceColumn.splice(source.index, 1);
        destinationColumn.splice(destination.index, 0, draggable);
        return {
          [source.column]: sourceColumn,
          [destination.column]: destinationColumn,
        };
      });
    }
  }

  function handleDragEnd() {
    setActiveDraggedCarrier(null);
  }

  if (query.isLoading) {
    return <Loader />;
  }

  return (
    <Container>
      {query.isError ? (
        <ErrorMessage
          error={query.error}
          defaultError="Data could not be loaded"
        />
      ) : null}
      {mutation.isError ? (
        <ErrorMessage
          error={mutation.error}
          defaultError="Error saving new order"
        />
      ) : null}
      <Toolbar disableGutters sx={{ justifyContent: "end" }}>
        <Button
          variant="primary"
          onClick={handleSubmit}
          loading={mutation.isPending}
        >
          Save
        </Button>
      </Toolbar>
      <DndContext
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <Grid container spacing={2}>
          <CarriersTable
            id={COLUMNS.SELECTED}
            carriers={state[COLUMNS.SELECTED]}
            showFooter={false}
            title={`${state[COLUMNS.SELECTED].length} carrier${
              state[COLUMNS.SELECTED].length > 1 ? "s" : ""
            } ordered`}
          />
          <CarriersTable
            id={COLUMNS.UNSELECTED}
            carriers={state[COLUMNS.UNSELECTED]}
            showIndex={false}
            title="Unordered Carriers"
          ></CarriersTable>
        </Grid>
        <DragOverlay>
          {activeDraggedCarrier ? (
            <CarrierTableRow
              name={activeDraggedCarrier.carrier.name}
              index={activeDraggedCarrier.index}
              showIndex={activeDraggedCarrier.showIndex}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </Container>
  );
}

type PrepareCarriersOrderResponse = {
  [COLUMNS.SELECTED]: Carrier[];
  [COLUMNS.UNSELECTED]: Carrier[];
};

function querySelect(data: Carrier[]): PrepareCarriersOrderResponse {
  return data.reduce(
    (acc, c) => {
      const columnToAppendTo = isCarrierSelected(c)
        ? COLUMNS.SELECTED
        : COLUMNS.UNSELECTED;
      acc[columnToAppendTo].push(c);
      return acc;
    },
    {
      [COLUMNS.SELECTED]: [],
      [COLUMNS.UNSELECTED]: [],
    },
  );
}

export default CarriersOrder;
